import dayjs, { Dayjs } from 'dayjs';
import { formatStandardTime } from 'Util/_HumanWritten/TimeUtils';
import { isNullOrUndefined } from '../TypeGuards';
import { whiteLabelStore } from 'Models/WhiteLabelStore';

export const formatStandardDate = (date?: Date | string) => {
	if (isNullOrUndefined(date)) {
		return date;
	}

	const dateObj = new Date(date);

	if (Number.isNaN(dateObj.getTime())) {
		console.warn('Invalid date provided:', date);
		return '';
	}

	// Always use local components since your server sends local datetime
	const year = dateObj.getFullYear();
	const month = (dateObj.getMonth() + 1).toString().padStart(2, '0');
	const day = dateObj.getDate().toString().padStart(2, '0');

	const format = whiteLabelStore.dateFormat;
	const localDateString = `${year}-${month}-${day}`;
	return dayjs(localDateString).format(format);
};

/**
 * The named-month patterns below in the client's own order, from its `dateFormat`: "8 Oct 2026" on a day-first site,
 * "Oct 8, 2026" on a month-first one, "2026 8 Oct" on a year-first one.
 */
const dateOrder = () => {
	const format = whiteLabelStore.dateFormat;
	return { monthFirst: format.indexOf('M') < format.indexOf('D'), yearFirst: format.startsWith('Y') };
};
const dayMonthPattern = () => (dateOrder().monthFirst ? 'MMM D' : 'D MMM');
const dayMonthYearPattern = () => {
	const { monthFirst, yearFirst } = dateOrder();
	if (yearFirst) return `YYYY ${dayMonthPattern()}`;
	return monthFirst ? 'MMM D, YYYY' : 'D MMM YYYY';
};
const monthYearPattern = (month: 'MMM' | 'MMMM') => (dateOrder().yearFirst ? `YYYY ${month}` : `${month} YYYY`);

/**
 * Returns date/time as "08:00 27/01/2025" OR "27/01/2025 08:00"
 * Time will be formatted depending on if it's military time or not
 */
export const formatStandardDateTime = (date: Date, dateFirst: boolean = true) => {
	if (isNullOrUndefined(date)) {
		return date;
	}

	const formattedDate = formatStandardDate(date);
	const formattedTime = formatStandardTime(date);

	return dateFirst ? `${formattedDate} ${formattedTime}` : `${formattedTime} ${formattedDate}`;
};

/**
 * Returns date as "Tue, Dec 02"
 */
export function formatDayMonthDate(date: Date) {
	return `${dayjs(date).format('ddd, MMM DD')}`;
}

/**
 * Returns date as "02 Dec 2025"
 */
export function formatDateMonthYear(date: Date) {
	return `${dayjs(date).format('DD MMM YYYY')}`;
}

/**
 * Returns date as "02 Dec 2025, 10:00"
 */
export function formatLongDateTime(date: Date) {
	const formattedTime = formatStandardTime(date);
	const formattedDate = formatDateMonthYear(date);
	return `${formattedDate}, ${formattedTime}`;
}

/**
 * Returns date and time as "16 Sep, 17:41" (or "16 Sep, 5:41PM" on a 12-hour site), with the year added —
 * "16 Sep 2025, 17:41" — only when it is not the current year. For dense lists where the full date reads
 * as noise.
 */
export function formatShortDateTime(date: Date | string) {
	const value = dayjs(date);
	const formattedDate = value.format(value.year() === dayjs().year() ? dayMonthPattern() : dayMonthYearPattern());
	return `${formattedDate}, ${formatStandardTime(value.toDate())}`;
}

/**
 * Returns time then weekday, day and month: "06:00 Thu 8 Oct" (or "6:00AM Thu 8 Oct" on a 12-hour site).
 * For a moment within a few days of today, where the year is noise and the weekday is what staff read.
 */
export function formatTimeWeekdayDayMonth(date: Date | string) {
	const value = dayjs(date);
	return `${formatStandardTime(value.toDate())} ${value.format(`ddd ${dayMonthPattern()}`)}`;
}

/**
 * Returns a date as "Thu 8 Oct" — a weekday beside the date, for a list of dates that all fall in one year.
 */
export function formatWeekdayDayMonth(date: Date | string) {
	return dayjs(date).format(`ddd ${dayMonthPattern()}`);
}

/**
 * Returns a date as "Thu 8 Oct 2026" — a weekday beside the date, for a header that names one sailing.
 */
export function formatWeekdayDayMonthYear(date: Date | string) {
	return dayjs(date).format(`ddd ${dayMonthYearPattern()}`);
}

/**
 * Returns a date and time as "Mon 12 Oct, 09:30", or "Mon 12 Oct 2026, 09:30" with the year — a stop's arrival or
 * departure on a trip.
 */
export function formatWeekdayDayMonthTime(date: Date | string, withYear = false) {
	const formattedDate = withYear ? formatWeekdayDayMonthYear(date) : formatWeekdayDayMonth(date);
	return `${formattedDate}, ${formatStandardTime(dayjs(date).toDate())}`;
}

/**
 * Returns a month as "October 2026" — a heading over a list of dates grouped by month.
 */
export function formatMonthYear(date: Date | string) {
	return dayjs(date).format(monthYearPattern('MMMM'));
}

/**
 * Returns a month as "Dec" — a tick on a month axis.
 */
export function formatShortMonth(date: Date | string) {
	return dayjs(date).format('MMM');
}

/**
 * Returns a month as "Dec 2027" — the ends of a span of months.
 */
export function formatShortMonthYear(date: Date | string) {
	return dayjs(date).format(monthYearPattern('MMM'));
}

/**
 * Returns a month on its own as "December" — a whole month of sailings, named.
 */
export function formatMonth(date: Date | string) {
	return dayjs(date).format('MMMM');
}

/**
 * Returns a date as "1 Sep", or "1 Sep 2027" outside this year — the date half of `formatShortDateTime`.
 */
export function formatShortDate(date: Date | string) {
	const value = dayjs(date);
	return value.format(value.year() === dayjs().year() ? dayMonthPattern() : dayMonthYearPattern());
}

/**
 * Returns a date as "1 Sep", never with its year — under a heading that already gives it.
 */
export function formatDayShortMonth(date: Date | string) {
	return dayjs(date).format(dayMonthPattern());
}

/**
 * Returns a date as "1 Sep 2027", always with its year — the ends of a span that crosses years.
 */
export function formatShortDateYear(date: Date | string) {
	return dayjs(date).format(dayMonthYearPattern());
}

/**
 * Returns the full weekday name, "Thursday".
 */
export function formatWeekday(date: Date | string) {
	return dayjs(date).format('dddd');
}

/**
 * Returns date as "02 Dec"
 */
export function formatDateMonth(date: Date) {
	const options: Intl.DateTimeFormatOptions = {
		day: '2-digit',
		month: 'short',
	};
	return (
		new Date(date)
			.toLocaleDateString('en-AU', options)
			//
			// Some older browsers add '.', so we remove them
			//
			.replace('.', '')
	);
}

export const isValidDate = (date: Date): boolean => {
	const parsedDate = new Date(date);
	return !Number.isNaN(parsedDate.getTime());
};

/**
 * True only when `date` is an instant we can actually use.
 *
 * Unlike {@link isValidDate}, a missing value is false: `new Date(null)` is the epoch rather than an
 * invalid date, so `isValidDate(null)` returns true and a null slips through as 1 Jan 1970. Use this
 * wherever a date may legitimately be absent — arithmetic on an absent date produces an Invalid
 * Date, which dayjs then formats as the literal string `Invalid Date`.
 */
export const isSetAndValidDate = (date: Date | null | undefined): date is Date => {
	if (date === null || date === undefined) {
		return false;
	}
	return !Number.isNaN(new Date(date).getTime());
};

function pad(value: number) {
	let r = String(value);
	if (r.length === 1) {
		r = `0${r}`;
	}
	return r;
}

/**
 * Format date as 2023-05-04T04:54:16.041Z
 *
 * This function ignores the local timezone in your computer.
 */
export function formatAsISO(date: Date) {
	return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(
		date.getMinutes(),
	)}:${pad(date.getSeconds())}.${String((date.getMilliseconds() / 1000).toFixed(3)).slice(2, 5)}Z`;
}

export const getCalendarDays = (date1: Date, date2: Date) => {
	// Extract only the date part (ignoring the time)
	const d1 = dayjs(date1).startOf('day');
	const d2 = dayjs(date2).startOf('day');
	const days = d2.diff(d1, 'days');
	return days;
};

/**
 * Returns date time unspecified timezone YYYY-MM-DDTHH:mm:ss (ISO 8601)
 */
export function toUnspecifiedTz(date: Date) {
	return dayjs(date).format('YYYY-MM-DDTHH:mm:ss');
}

export function nowUnspecifiedTz() {
	return dayjs(toUnspecifiedTz(new Date())).toDate();
}

export function isInvalidDate(date: Date) {
	const isInvalid = Number.isNaN(date?.getTime());

	if (isInvalid) {
		console.error('Invalid date:', date);
	}

	return isInvalid;
}

export const isDateInPast = (dateToCheck: Dayjs | Date | string): boolean => {
	const date = dayjs(dateToCheck);
	// date.isBefore() compares the date object to the current moment by default
	return date.isBefore(dayjs());
};

/**
 * Formats two dates into a user-friendly range string, omitting redundant parts.
 * @param startDate The start date (Dayjs object or ISO string).
 * @param endDate The end date (Dayjs object or ISO string).
 * @returns A user-friendly date range string.
 */
export const formatFriendlyDateRange = (startDate: dayjs.ConfigType, endDate: dayjs.ConfigType): string => {
	const start = dayjs(startDate);
	const end = dayjs(endDate);

	// Full formats
	const fullStartFormat = 'D MMMM, YYYY'; // e.g., 1 December, 2025
	const fullEndFormat = 'D MMMM, YYYY'; // e.g., 31 December, 2025

	// Short formats
	const startDayFormat = 'D'; // e.g., 1
	const startMonthDayFormat = 'D MMMM'; // e.g., 1 December

	const endFormat = 'D MMMM, YYYY'; // e.g., 31 December, 2025

	// Case 1: Same Year
	if (start.isSame(end, 'year')) {
		// Case 1A: Same Month
		if (start.isSame(end, 'month')) {
			// Dec 1 – 3, 2025
			return `${start.format(startDayFormat)} – ${end.format(endFormat)}`;
		}

		// Dec 1 – 31 Dec, 2025
		return `${start.format(startMonthDayFormat)} – ${end.format(endFormat)}`;
	}

	// Case 2: Different Years (Full date range required)
	// 1 December, 2025 – 1 February, 2026
	return `${start.format(fullStartFormat)} – ${end.format(fullEndFormat)}`;
};
